import React, { forwardRef, useId } from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VARIANTS = {
  outline:
    'bg-white border border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  filled:
    'bg-gray-50 border border-gray-300 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  unstyled:
    'bg-transparent border-transparent focus:border-transparent focus:ring-0',
};

const SIZE = {
  sm: 'text-sm py-1.5 px-2.5',
  md: 'text-sm py-2 px-3',
  lg: 'text-base py-2.5 px-4',
};

const BASE =
  'block w-full rounded-md transition-colors placeholder:text-gray-400 focus:outline-none resize-y';
const DISABLED = 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';

const TextArea = forwardRef(function TextArea(
  {
    label,
    helperText,
    error,
    required = false,
    id: idProp,
    name,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    autoFocus,
    maxLength,
    rows = 4,
    variant = 'outline',
    size = 'md',
    fullWidth = true,
    className,
    textareaClassName,
    containerClassName,
    style,
    disabled = false,
    readOnly = false,
    resize = true,
    autoResize = false,
    minRows = 2,
    maxRows = 10,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const id = idProp || `${name || 'textarea'}-${reactId}`;

  const describedByIds = [];
  if (helperText) describedByIds.push(`${id}-help`);
  if (error) describedByIds.push(`${id}-error`);

  const sizeClasses = SIZE[size];
  const variantClasses = VARIANTS[variant];
  const errorClasses = error
    ? 'border-red-500 focus:border-red-600 focus:ring-red-600/20'
    : '';
  
  const resizeClasses = !resize ? 'resize-none' : '';

  const textareaClasses = cn(
    BASE,
    sizeClasses,
    variantClasses,
    DISABLED,
    errorClasses,
    resizeClasses,
    textareaClassName
  );

  // Character count
  const showCharCount = maxLength && value !== undefined;
  const charCount = value?.length || 0;

  return (
    <div
      className={cn(fullWidth ? 'w-full' : '', containerClassName, className)}
      style={style}
    >
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}

      <div className="relative">
        <textarea
          id={id}
          name={name}
          ref={ref}
          rows={rows}
          className={textareaClasses}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedByIds.length ? describedByIds.join(' ') : undefined}
          autoFocus={autoFocus}
          maxLength={maxLength}
          {...rest}
        />
        
        {showCharCount && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-500 bg-white px-1 rounded">
            {charCount}/{maxLength}
          </div>
        )}
      </div>

      {helperText && !error && (
        <p id={`${id}-help`} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {typeof error === 'string' ? error : 'Invalid value'}
        </p>
      )}
    </div>
  );
});

TextArea.displayName = 'TextArea';

export default TextArea;